import React from 'react'
import { TableContainer } from '../../../../components/table/TableContainer'
import { MultiLineChart } from './MultiLineChart'
import { TotalTrendChart } from './TotalTrendChart'
import { TwoLineTrendChart } from './TwoLineTrendChart'
import { RankingList } from './RankingList'
import { DonutChart } from './DonutChart'
import { StatCard, Group, Card } from './Common'
import { formatBs, topNConOtros } from './utils'

const bsColumn = (field) => ({
    custom: (item) => (
        <div className='flex justify-end'>
            <div className='dark:bg-gray-800 bg-green-100 dark:text-gray-300 text-gray-700 px-1 font-semibold rounded-sm'>
                Bs {formatBs(item[field] ?? 0)}
            </div>
        </div>
    ),
})

// Una garantía puede estar pendiente solo en envases (efectivo=0) — por eso siempre se muestran
// ambas columnas: cuántos envases y cuánto efectivo, para que "0" no se confunda con "no debe nada".
const garantiaResponsableHeaders = [
    { label: 'Responsable', columns: ['nombre'] },
    { label: 'Cantidad (envases)', columns: ['cantidad'], numberValue: true },
    { label: 'Efectivo', ...bsColumn('total') },
]

const prestamoResponsableHeaders = [
    { label: 'Responsable', columns: ['nombre'] },
    { label: 'Cantidad (envases)', columns: ['cantidad'], numberValue: true },
]

const CardCaption = ({ children }) => (
    <div className='text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2'>{children}</div>
)

export const EmpresaDetalle = ({ empresa }) => {
    if (!empresa?.ok) {
        return (
            <div className='bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 px-4 py-3 rounded-md text-sm'>
                <i className='fa fa-triangle-exclamation mr-1' />
                No se pudo obtener datos de {empresa?.nombre}. {empresa?.error}
            </div>
        )
    }

    const participacionChoferes = topNConOtros(empresa.ventasPorChofer ?? [], 5)
    const participacionPuntosVenta = topNConOtros(empresa.ventasPorPuntoVenta ?? [], 5)
    const gastosPorConceptoTop5 = topNConOtros(empresa.gastosPorConcepto ?? [], 5)

    return (
        <div className='grid gap-10 min-w-0'>
            <Group icon='fa-sack-dollar' title='Ventas' color='teal'>
                <StatCard icon='fa-sack-dollar' label='Total de ventas' value={`Bs ${formatBs(empresa.ventaTotal)}`} color='teal' />

                <Card>
                    <TotalTrendChart title='VENTAS POR DÍA' serieData={empresa.serieVentaTotal} />
                </Card>

                <div className='grid grid-cols-1 lg:grid-cols-2 gap-5 min-w-0'>
                    <Card>
                        <MultiLineChart title='COMPARATIVO DE CHÓFERES' serieData={empresa.serieVentasChoferes} />
                    </Card>
                    <Card>
                        <MultiLineChart title='COMPARATIVO DE PUNTOS DE VENTA' serieData={empresa.serieVentasPuntosVenta} />
                    </Card>
                </div>

                <div className='grid grid-cols-1 lg:grid-cols-2 gap-5 min-w-0'>
                    <Card>
                        <CardCaption>RANKING CHÓFERES</CardCaption>
                        <RankingList items={empresa.ventasPorChofer} emptyText='Sin ventas registradas por chóferes' />
                    </Card>
                    <Card>
                        <CardCaption>RANKING PUNTOS DE VENTA</CardCaption>
                        <RankingList items={empresa.ventasPorPuntoVenta} emptyText='Sin ventas registradas por puntos de venta' />
                    </Card>
                    <Card>
                        <DonutChart title='PARTICIPACIÓN: CHÓFERES' labels={participacionChoferes.labels} data={participacionChoferes.data} />
                    </Card>
                    <Card>
                        <DonutChart title='PARTICIPACIÓN: PUNTOS DE VENTA' labels={participacionPuntosVenta.labels} data={participacionPuntosVenta.data} />
                    </Card>
                </div>

                <div className='grid gap-2 min-w-0'>
                    <CardCaption>VENTAS POR TIPO DE CLIENTE (choferes/puntos de venta arriba solo cuentan ventas a clientes regulares)</CardCaption>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-5 min-w-0'>
                        <StatCard icon='fa-user' label='Ventas a clientes' value={`Bs ${formatBs(empresa.totalVentasClientes)}`} color='teal' />
                        <StatCard icon='fa-building' label='Ventas a empresas y mayoristas' value={`Bs ${formatBs(empresa.totalVentasEmpresasYMayoristas)}`} color='teal' />
                    </div>
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-5 min-w-0'>
                        <Card>
                            <CardCaption>RANKING VENDEDORES: VENTAS A EMPRESAS/MAYORISTAS</CardCaption>
                            <RankingList items={empresa.ventasEmpresasYMayoristasPorVendedor} emptyText='Sin ventas registradas a empresas/mayoristas' />
                        </Card>
                        <Card>
                            <DonutChart
                                title='CLIENTES VS EMPRESAS/MAYORISTAS'
                                labels={['Clientes', 'Empresas y mayoristas']}
                                data={[empresa.totalVentasClientes ?? 0, empresa.totalVentasEmpresasYMayoristas ?? 0]}
                            />
                        </Card>
                    </div>
                </div>
            </Group>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-5 min-w-0'>
                <Card>
                    <DonutChart
                        title='VENTAS VS DEUDAS (TOTAL DEL RANGO)'
                        labels={['Ventas', 'Deudas']}
                        data={[empresa.ventaTotal ?? 0, empresa.deudaTotal ?? 0]}
                    />
                </Card>
                <Card>
                    <TwoLineTrendChart
                        title='VENTAS VS DEUDA GENERADA POR PERIODO'
                        serieA={empresa.serieVentaTotal}
                        serieB={empresa.serieDeudaTotal}
                        labelA='Ventas'
                        labelB='Deuda generada'
                        colorA='#06d6a0'
                        colorB='#ef476f'
                    />
                </Card>
            </div>


            <Group icon='fa-money-bill-wave' title='Gastos' color='orange'>
                <StatCard icon='fa-money-bill-wave' label='Total de gastos' value={`Bs ${formatBs(empresa.totalGastos)}`} color='orange' />

                <Card>
                    <TwoLineTrendChart
                        title='GASTOS: CHÓFERES VS PUNTOS DE VENTA POR PERIODO'
                        serieA={empresa.serieGastosChoferes}
                        serieB={empresa.serieGastosPuntosVenta}
                        labelA='Chóferes'
                        labelB='Puntos de venta'
                        colorA='#ff9f1c'
                        colorB='#118ab2'
                    />
                </Card>

                <div className='grid grid-cols-1 lg:grid-cols-2 gap-5 min-w-0'>
                    <Card>
                        <CardCaption>RANKING CHÓFERES (MÁS GASTOS)</CardCaption>
                        <RankingList items={empresa.gastosPorChofer} emptyText='Sin gastos registrados por chóferes' />
                    </Card>
                    <Card>
                        <CardCaption>RANKING PUNTOS DE VENTA (MÁS GASTOS)</CardCaption>
                        <RankingList items={empresa.gastosPorPuntoVenta} emptyText='Sin gastos registrados por puntos de venta' />
                    </Card>
                </div>

                <div className='grid grid-cols-1 lg:grid-cols-2 gap-5 min-w-0'>
                    <Card>
                        <CardCaption>TODOS LOS GASTOS POR CONCEPTO</CardCaption>
                        <RankingList items={empresa.gastosPorConcepto} emptyText='Sin gastos registrados' />
                    </Card>
                    <Card>
                        <DonutChart
                            title='GASTOS POR CONCEPTO (TOP 5 + OTROS)'
                            labels={gastosPorConceptoTop5.labels}
                            data={gastosPorConceptoTop5.data}
                        />
                    </Card>
                </div>
            </Group>

            <Group icon='fa-file-invoice-dollar' title='Deudas' color='red'>
                {/* <StatCard icon='fa-file-invoice-dollar' label='Total de deudas' value={`Bs ${formatBs(empresa.deudaTotal)}`} color='red' /> */}
                        <StatCard icon='fa-user' label='Deudas de clientes' value={`Bs ${formatBs(empresa.totalDeudasClientes)}`} color='red' />
                <Card>
                    <CardCaption>DEUDAS POR RESPONSABLE (CLIENTES REGULARES, DE MAYOR A MENOR)</CardCaption>
                    <RankingList items={empresa.deudasPorResponsable} emptyText='Sin deudas registradas' />
                </Card>

                <div className='grid gap-2 min-w-0'>
                    <CardCaption>DEUDAS POR TIPO DE CLIENTE</CardCaption>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-5 min-w-0'>
                        <StatCard icon='fa-building' label='Deudas de empresas y mayoristas' value={`Bs ${formatBs(empresa.totalDeudasEmpresasYMayoristas)}`} color='red' />
                    </div>
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-5 min-w-0'>
                        <Card>
                            <CardCaption>RANKING VENDEDORES: DEUDAS DE EMPRESAS/MAYORISTAS</CardCaption>
                            <RankingList items={empresa.deudasEmpresasYMayoristasPorVendedor} emptyText='Sin deudas registradas de empresas/mayoristas' />
                        </Card>
                        <Card>
                            <DonutChart
                                title='CLIENTES VS EMPRESAS/MAYORISTAS'
                                labels={['Clientes', 'Empresas y mayoristas']}
                                data={[empresa.totalDeudasClientes ?? 0, empresa.totalDeudasEmpresasYMayoristas ?? 0]}
                            />
                        </Card>
                    </div>
                </div>
            </Group>

            <Group icon='fa-shield-halved' title='Garantías y envases' color='blue'>
                <div className='grid grid-cols-1 sm:grid-cols-3 gap-5 min-w-0'>
                    <StatCard icon='fa-shield-halved' label='Garantías pendientes (Bs)' value={`Bs ${formatBs(empresa.garantiaTotal)}`} color='blue' />
                    <StatCard icon='fa-boxes-stacked' label='Envases prestados' value={`${empresa.envasesPrestadosTotal ?? 0}`} color='yellow' />
                    <StatCard icon='fa-boxes-stacked' label='Envases en garantía' value={`${empresa.envasesGarantiaTotal ?? 0}`} color='blue' />
                </div>
                    <Card>
                        <DonutChart
                            title='ENVASES: PRÉSTAMO VS GARANTÍA'
                            labels={['Prestados', 'En garantía']}
                            data={[empresa.envasesPrestadosTotal ?? 0, empresa.envasesGarantiaTotal ?? 0]}
                            valueFormatter={(v) => `${v} envases`}
                        />
                    </Card>
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-5 min-w-0'>
                        <Card>
                            <DonutChart
                                title='ENVASES PRESTADOS: 100L VS 200L'
                                labels={['100L', '200L']}
                                data={[empresa.envasesPrestadosDe100 ?? 0, empresa.envasesPrestadosDe200 ?? 0]}
                                valueFormatter={(v) => `${v} envases`}
                            />
                        </Card>
                        <Card>
                            <DonutChart
                                title='ENVASES EN GARANTÍA: 100L VS 200L'
                                labels={['100L', '200L']}
                                data={[empresa.envasesGarantiaDe100 ?? 0, empresa.envasesGarantiaDe200 ?? 0]}
                                valueFormatter={(v) => `${v} envases`}
                            />
                        </Card>
                    </div>

                <div className='grid gap-2 min-w-0'>
                    <CardCaption>GARANTÍAS POR TIPO DE CLIENTE</CardCaption>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-5 min-w-0'>
                        <StatCard icon='fa-user' label='Garantías de clientes' value={`Bs ${formatBs(empresa.totalGarantiasClientes)}`} color='blue' />
                        <StatCard icon='fa-building' label='Garantías de empresas y mayoristas' value={`Bs ${formatBs(empresa.totalGarantiasEmpresasYMayoristas)}`} color='blue' />
                    </div>
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-5 min-w-0'>
                        <Card>
                        <CardCaption>GARANTÍAS POR RESPONSABLE (CLIENTES REGULARES)</CardCaption>
                        <TableContainer headers={garantiaResponsableHeaders} data={empresa.garantiasPorResponsable} />
                    </Card>
                        <Card>
                            <CardCaption>RESPONSABLES: GARANTÍAS DE EMPRESAS/MAYORISTAS</CardCaption>
                            <TableContainer headers={garantiaResponsableHeaders} data={empresa.garantiasEmpresasYMayoristasPorResponsable} />
                        </Card>
                    </div>
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-5 min-w-0'>
                        <Card>
                            <DonutChart
                                title='EFECTIVO EN GARANTÍA: CLIENTES VS EMPRESAS/MAYORISTAS'
                                labels={['Clientes', 'Empresas y mayoristas']}
                                data={[empresa.totalGarantiasClientes ?? 0, empresa.totalGarantiasEmpresasYMayoristas ?? 0]}
                            />
                        </Card>
                        <Card>
                            <DonutChart
                                title='ENVASES EN GARANTÍA: CLIENTES VS EMPRESAS/MAYORISTAS'
                                labels={['Clientes', 'Empresas y mayoristas']}
                                data={[empresa.totalEnvasesGarantiaClientes ?? 0, empresa.totalEnvasesGarantiaEmpresasYMayoristas ?? 0]}
                                valueFormatter={(v) => `${v} envases`}
                            />
                        </Card>
                    </div>
                </div>

                <div className='grid gap-2 min-w-0'>
                    <CardCaption>PRÉSTAMOS DE ENVASES POR VENDEDOR (QUIÉN TIENE PENDIENTE DE COBRAR/RECOGER)</CardCaption>
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-5 min-w-0'>
                        <Card>
                            <CardCaption>CLIENTES REGULARES</CardCaption>
                            <TableContainer headers={prestamoResponsableHeaders} data={empresa.prestamosPorResponsable} />
                        </Card>
                        <Card>
                            <CardCaption>CLIENTES TIPO EMPRESA/MAYORISTA</CardCaption>
                            <TableContainer headers={prestamoResponsableHeaders} data={empresa.prestamosEmpresasYMayoristasPorResponsable} />
                        </Card>
                    </div>
                    <Card>
                        <DonutChart
                            title='ENVASES PRESTADOS: CLIENTES VS EMPRESAS/MAYORISTAS'
                            labels={['Clientes', 'Empresas y mayoristas']}
                            data={[empresa.totalPrestamosClientes ?? 0, empresa.totalPrestamosEmpresasYMayoristas ?? 0]}
                            valueFormatter={(v) => `${v} envases`}
                        />
                    </Card>
                </div>
            </Group>
        </div>
    )
}
